import { Avatar as AntAvatar, type AvatarProps as AntAvatarProps } from 'antd'
import { forwardRef } from 'react'
import { useUiBaseConfig } from './UiBaseProvider'

type Color = 'primary' | 'purple'

type MapColors = Record<
  Color,
  {
    backgroundColor: string
    color: string
  }
>

const useAvatarColors = (color: Color) => {
  const { token } = useUiBaseConfig()
  const mapColors: MapColors = {
    primary: {
      backgroundColor: token.primary1,
      color: token.primary5,
    },
    purple: {
      backgroundColor: token.purple1,
      color: token.purple5,
    },
  }
  return mapColors[color]
}

export type AvatarProps = { color?: Color } & AntAvatarProps

const InternalAvatar = forwardRef<HTMLSpanElement, AvatarProps>(
  ({ color = 'primary', size = 'default', ...props }, ref) => {
    const colorConfig = useAvatarColors(color)
    return (
      <AntAvatar
        ref={ref}
        {...props}
        size={size}
        style={{
          color: colorConfig.color,
          backgroundColor: colorConfig.backgroundColor,
          ...props.style,
        }}
      />
    )
  }
)

type AvatarGroupProps = {
  color?: Color
} & React.ComponentProps<typeof AntAvatar.Group>

const InternalAvatarGroup: React.FC<AvatarGroupProps> = ({ color = 'primary', ...props }) => {
  const colorConfig = useAvatarColors(color)
  return (
    <AntAvatar.Group
      {...props}
      max={{
        ...props.max,
        style: {
          color: colorConfig.color,
          backgroundColor: colorConfig.backgroundColor,
          ...props.max?.style,
        },
      }}
    />
  )
}

type CompoundedComponent = typeof InternalAvatar & {
  Group: typeof InternalAvatarGroup
}

export const Avatar = InternalAvatar as CompoundedComponent

Avatar.Group = InternalAvatarGroup
